<template>
  <div class="page">
    <h2>理疗预约管理</h2>
    <div class="toolbar">
      <select v-model="filters.status">
        <option value="">全部状态</option>
        <option v-for="s in statuses" :key="s" :value="s">{{ statusMap[s] || s }}</option>
      </select>
      <select v-model="filters.rescheduleStatus">
        <option value="">改期：全部</option>
        <option value="PENDING">改期待审</option>
        <option value="APPROVED">改期已通过</option>
        <option value="REJECTED">改期已驳回</option>
      </select>
      <input v-model.number="filters.therapistId" type="number" placeholder="理疗师ID" />
      <button :disabled="loading" @click="loadData">查询</button>
    </div>
    <table>
      <thead>
        <tr><th>预约号</th><th>活动</th><th>用户</th><th>时间</th><th>支付</th><th>状态</th><th>改期</th><th>操作</th></tr>
      </thead>
      <tbody>
        <tr v-for="item in list" :key="item.id">
          <td>{{ item.bookingNo }}</td>
          <td>{{ item.therapy?.name }}<template v-if="item.activitySkuName"> · {{ item.activitySkuName }}</template></td>
          <td>{{ bookingUserLine(item) }}</td>
          <td>{{ item.bookingDate }} {{ item.startTime }}</td>
          <td>{{ item.paymentSource === 'BUNDLE' ? '套餐' : '普通' }}</td>
          <td>
            <span :class="['status-badge', 'status-' + item.status.toLowerCase()]">
              {{ displayBookingStatus(item) }}
            </span>
          </td>
          <td>{{ rescheduleStatusLabel(item) }}</td>
          <td>
            <button :disabled="loading" v-if="item.rescheduleStatus === 'PENDING'" @click="openRescheduleApprove(item)">改期审批</button>
            <button :disabled="loading" v-if="item.rescheduleStatus === 'PENDING'" @click="rejectReschedule(item.id)">驳回改期</button>
            <button :disabled="loading" v-if="item.status === 'PENDING_PAYMENT' && item.paymentSource !== 'BUNDLE'" @click="confirmPayment(item.id)">确认收款</button>
            <button :disabled="loading" v-if="item.status === 'PAID'" @click="openApprove(item)">审批排班</button>
            <button :disabled="loading" v-if="item.status === 'PAID'" @click="reject(item.id)">拒绝</button>
            <button :disabled="loading" v-if="item.status === 'CONFIRMED'" @click="verify(item.id)">核销</button>
            <button :disabled="loading" v-if="item.status === 'CHECKED_IN'" @click="complete(item.id)">完成</button>
            <button :disabled="loading" v-if="item.status !== 'COMPLETED' && item.status !== 'CANCELLED'" @click="cancel(item.id)">取消</button>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="showApprove" class="dialog-mask">
      <div class="dialog">
        <h3>审批排班</h3>
        <div v-if="approveRow" class="approve-summary">
          <p>理疗师：{{ approveRow.therapist?.name || '—' }}</p>
          <p>时段：{{ approveRow.bookingDate }} {{ approveRow.startTime }} - {{ approveRow.endTime }}</p>
          <p v-if="approveRow.scheduleId != null">排班编号：{{ approveRow.scheduleId }}</p>
        </div>
        <p class="auto-room-tip">系统将根据房间容量与项目规则自动分配房间。</p>
        <textarea v-model="approveForm.confirmationNotes" placeholder="注意事项"></textarea>
        <div class="actions">
          <button :disabled="loading" @click="showApprove=false">取消</button>
          <button :disabled="loading" @click="approve">确定</button>
        </div>
      </div>
    </div>

    <div v-if="showRescheduleApprove" class="dialog-mask">
      <div class="dialog">
        <h3>改期审批</h3>
        <div v-if="rescheduleApproveRow" class="approve-summary">
          <p>原定：{{ rescheduleApproveRow.rescheduleOriginalDate || rescheduleApproveRow.bookingDate }}
            {{ rescheduleApproveRow.rescheduleOriginalStartTime || rescheduleApproveRow.startTime }}</p>
          <p>申请改为：{{ rescheduleApproveRow.pendingBookingDate }} {{ rescheduleApproveRow.pendingStartTime }}</p>
          <p v-if="rescheduleApproveRow.rescheduleReason">原因：{{ rescheduleApproveRow.rescheduleReason }}</p>
        </div>
        <div class="actions">
          <button :disabled="loading" @click="showRescheduleApprove=false">取消</button>
          <button :disabled="loading" @click="approveReschedule">通过</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { therapyBookingService } from '@/services/therapyBookingService'

export default {
  name: 'TherapyBookingManagement',
  data() {
    return {
      list: [],
      statuses: ['PENDING_PAYMENT', 'PAID', 'CONFIRMED', 'CHECKED_IN', 'COMPLETED', 'CANCELLED'],
      statusMap: { PENDING_PAYMENT: '待付款', PAID: '已付款', CONFIRMED: '已预约', CHECKED_IN: '已签到', COMPLETED: '已完成', CANCELLED: '已取消' },
      filters: { status: '', therapistId: null, rescheduleStatus: '' },
      showApprove: false,
      showRescheduleApprove: false,
      approveId: null,
      approveRow: null,
      rescheduleApproveId: null,
      rescheduleApproveRow: null,
      approveForm: { confirmationNotes: '' },
      loading: false
    }
  },
  mounted() {
    this.applyRouteFilters()
    this.loadData()
  },
  watch: {
    '$route.query.status'() {
      this.applyRouteFilters()
      this.loadData()
    }
  },
  methods: {
    therapyBookingGenderZh(g) {
      if (g === 'MALE') return '男'
      if (g === 'FEMALE') return '女'
      return ''
    },
    bookingUserLine(item) {
      const name = item.contactName || '—'
      const g = this.therapyBookingGenderZh(item.contactGender)
      const bd = item.contactBirthDate ? String(item.contactBirthDate).slice(0, 10) : ''
      const tail = [g, bd].filter(Boolean).join(' · ')
      return tail ? `${name} · ${tail}` : name
    },
    applyRouteFilters() {
      const status = this.$route.query.status
      if (status && this.statuses.includes(status)) {
        this.filters.status = status
      }
    },
    displayBookingStatus(item) {
      if (item.statusLabel) return item.statusLabel
      if (item.paymentSource === 'BUNDLE') {
        const bundleMap = {
          PAID: '待排班确认',
          COMPLETED: '已完成（次数已用）'
        }
        if (bundleMap[item.status]) return bundleMap[item.status]
      }
      return this.statusMap[item.status] || item.status
    },
    rescheduleStatusLabel(item) {
      const map = {
        PENDING: '待审批',
        APPROVED: '已通过',
        REJECTED: '已驳回',
        NONE: '—'
      }
      return map[item.rescheduleStatus] || '—'
    },
    async loadData() {
      this.loading = true
      try {
        const res = await therapyBookingService.fetchList({ ...this.filters, size: 100 })
        this.list = res.data?.content || []
      } catch (e) {
        alert(e?.response?.data?.error || '加载预约列表失败')
      } finally {
        this.loading = false
      }
    },
    async confirmPayment(id) {
      this.loading = true
      try {
        await therapyBookingService.confirmPayment(id)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '确认收款失败')
      } finally {
        this.loading = false
      }
    },
    async verify(id) {
      this.loading = true
      try {
        await therapyBookingService.verify(id)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '核销失败')
      } finally {
        this.loading = false
      }
    },
    async complete(id) {
      this.loading = true
      try {
        await therapyBookingService.complete(id)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '标记完成失败')
      } finally {
        this.loading = false
      }
    },
    openApprove(item) {
      this.approveId = item.id
      this.approveRow = item
      this.approveForm = { confirmationNotes: '' }
      this.showApprove = true
    },
    openRescheduleApprove(item) {
      this.rescheduleApproveId = item.id
      this.rescheduleApproveRow = item
      this.showRescheduleApprove = true
    },
    async approveReschedule() {
      this.loading = true
      try {
        await therapyBookingService.approveReschedule(this.rescheduleApproveId, {})
        this.showRescheduleApprove = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '改期审批失败')
      } finally {
        this.loading = false
      }
    },
    async rejectReschedule(id) {
      const reason = window.prompt('请输入驳回原因')
      if (reason === null) return
      const trimmed = reason.trim()
      if (!trimmed) {
        alert('驳回原因不能为空')
        return
      }
      this.loading = true
      try {
        await therapyBookingService.rejectReschedule(id, trimmed)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '驳回改期失败')
      } finally {
        this.loading = false
      }
    },
    async approve() {
      this.loading = true
      try {
        await therapyBookingService.approve(this.approveId, {
          confirmationNotes: this.approveForm.confirmationNotes
        })
        this.showApprove = false
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '审批排班失败')
      } finally {
        this.loading = false
      }
    },
    async cancel(id) {
      const reason = window.prompt('请输入取消原因')
      if (reason === null) return
      const trimmedReason = reason.trim()
      if (!trimmedReason) {
        alert('取消原因不能为空')
        return
      }
      this.loading = true
      try {
        await therapyBookingService.cancel(id, trimmedReason)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '取消预约失败')
      } finally {
        this.loading = false
      }
    },
    async reject(id) {
      const reason = window.prompt('请输入拒绝原因')
      if (reason === null) return
      const trimmedReason = reason.trim()
      if (!trimmedReason) {
        alert('拒绝原因不能为空')
        return
      }
      this.loading = true
      try {
        await therapyBookingService.reject(id, trimmedReason)
        await this.loadData()
      } catch (e) {
        alert(e?.response?.data?.error || '拒绝预约失败')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar select,
.toolbar input {
  height: 40px;
}
.toolbar select {
  min-width: 120px;
}
.approve-summary {
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 6px;
  font-size: 14px;
  line-height: 1.6;
}
.approve-summary p {
  margin: 4px 0;
}
.auto-room-tip {
  margin: 0 0 12px;
  color: #666;
  font-size: 14px;
}
</style>
